import React, { useState, useEffect } from "react";
import {
    Box,
    Heading,
    VStack,
    HStack,
    Text,
    Card,
    CardBody,
    List,
    ListItem,
    ListIcon,
    Spinner,
    Alert,
    AlertIcon,
    Divider,
    Icon,
    SimpleGrid,
    Wrap,
    WrapItem,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    useToast,
    useColorMode,
    useColorModeValue,
} from "@chakra-ui/react";
import { Button, IconButton, Select, Badge } from "./components/ui";
import { tierWords } from "./utils/statusWords";
import { CheckCircleIcon, LockIcon, RepeatIcon, ChatIcon, AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { brandConfig, lightTheme, darkTheme, getColor } from "../../brandConfig";
import { usePageTitle } from "../../hooks/useDocumentTitle";
import { useAuth } from "../../contexts/AuthContext";
import { UnifiedLoginModal } from "../authentication/components/UnifiedLoginModal";
import { StripePaymentMethodSetup } from "./components/StripePaymentMethodSetup";
import { PaymentMethod as PaymentMethodType, SubscriptionProduct as SubscriptionProductType } from "../../generated/graphql";
import {
    GET_PRODUCT_BY_SLUG,
    GET_ALL_PRODUCTS_PUBLIC as GET_ALL_PRODUCTS,
    GET_MY_PAYMENT_METHODS,
    GET_TENANT_STRIPE_PUBLIC_KEY,
    CREATE_SETUP_INTENT,
    CREATE_LOCAL_SUBSCRIPTION,
    CREATE_PAYMENT_METHOD,
    VALIDATE_COUPON,
} from "./subscriptionQueries";
import { REGISTER_WITH_VERIFIED_EMAIL } from "../authentication/queries";
import {
    enrollPasskey,
    browserSupportsWebAuthn,
    friendlyPasskeyError,
    GENERATE_PASSKEY_REGISTRATION_OPTIONS,
    VERIFY_PASSKEY_REGISTRATION,
} from "../authentication";
import { GET_MY_ENROLLMENTS_MINIMAL, GET_MY_REWARD_BALANCE, GET_PUBLIC_REWARD_TOKEN_CONFIG } from "./queries";
import { Input } from "@chakra-ui/react";

// ─── Types ──────────────────────────────────────────────────────────────────

type ActivationStep = 'info' | 'payment' | 'activating' | 'success';

// ─── Component ──────────────────────────────────────────────────────────────

const SubscribePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useToast();
    const tenantId = brandConfig.tenantId;
    const { isAuthenticated, user, refreshAuth } = useAuth();

    const [step, setStep] = useState<ActivationStep>('info');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [activationResult, setActivationResult] = useState<{
        subscriptionId?: string;
        enrollmentId?: string;
        courseStartUrl?: string;
    } | null>(null);
    const [, setSavedPaymentMethodId] = useState<string | null>(null);

    // ─── Seamless self-signup (new email at checkout) ─────────────────────
    // When the login modal verifies an email with no account, we collect a name
    // (+ optional phone) here and create the account via registerWithVerifiedEmail,
    // then continue the original activate/payment flow. Passwordless — we nudge a
    // passcode on /security afterward.
    const [pendingSignup, setPendingSignup] = useState<{ email: string; code: string } | null>(null);
    const [signupFName, setSignupFName] = useState("");
    const [signupLName, setSignupLName] = useState("");
    const [signupPhone, setSignupPhone] = useState("");
    const [showPasscodeNudge, setShowPasscodeNudge] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);
    const [passkeyAdded, setPasskeyAdded] = useState(false);

    // ─── Coupon / promo code ──────────────────────────────────────────────
    const [couponInput, setCouponInput] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState<{
        code: string; discountApplied: number; finalAmount: number;
    } | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [validateCoupon, { loading: couponChecking }] = useLazyQuery(VALIDATE_COUPON, {
        fetchPolicy: "network-only",
    });

    // ─── Reward tokens (optional payment method — J2, 2026-08-04) ───────────
    // Only meaningful for a chargeOnActivation product (there's an actual
    // charge to discount); the backend guards this the same way. Rides
    // alongside a coupon rather than replacing it — the token discount is
    // applied to whatever price remains after the coupon.
    const [tokenCountToApply, setTokenCountToApply] = useState(0);

    // ─── Queries ────────────────────────────────────────────────────────────

    const { data, loading, error } = useQuery(GET_PRODUCT_BY_SLUG, {
        variables: { tenantId, slug: slug || "" },
        skip: !tenantId || !slug,
    });

    const { data: allProductsData } = useQuery(GET_ALL_PRODUCTS, {
        variables: { tenantId },
        skip: !tenantId,
    });

    const { data: paymentMethodsData, refetch: refetchPaymentMethods } = useQuery(GET_MY_PAYMENT_METHODS, {
        skip: !isAuthenticated,
    });

    const { data: stripeKeyData } = useQuery(GET_TENANT_STRIPE_PUBLIC_KEY, {
        skip: !isAuthenticated,
    });

    const { data: rewardBalanceData } = useQuery(GET_MY_REWARD_BALANCE, {
        skip: !isAuthenticated,
    });
    const { data: tokenConfigData } = useQuery(GET_PUBLIC_REWARD_TOKEN_CONFIG);

    // Reload-resilience: the user's course enrolments (source of truth = server, not browser
    // storage) so a reload after activation can land them back on "you're in".
    const { data: myEnrollmentsData } = useQuery(GET_MY_ENROLLMENTS_MINIMAL, {
        skip: !isAuthenticated,
        fetchPolicy: "cache-and-network",
    });

    const [createSetupIntent, { data: setupIntentData, loading: creatingSetup }] = useMutation(CREATE_SETUP_INTENT);

    const [createLocalSubscription, { loading: activating }] = useMutation(CREATE_LOCAL_SUBSCRIPTION);
    const [createPaymentMethod] = useMutation(CREATE_PAYMENT_METHOD);
    const [registerWithVerifiedEmail, { loading: registering }] = useMutation(REGISTER_WITH_VERIFIED_EMAIL);
    const [generatePasskeyRegOptions] = useMutation(GENERATE_PASSKEY_REGISTRATION_OPTIONS);
    const [verifyPasskeyRegistration] = useMutation(VERIFY_PASSKEY_REGISTRATION);

    const product = data?.publicSubscriptionProductBySlug;
    const allProducts: Pick<SubscriptionProductType, 'id' | 'name' | 'slug' | 'amount' | 'interval' | 'currency' | 'badge' | 'chargeOnActivation'>[] = allProductsData?.publicSubscriptionProducts || [];
    const paymentMethods: Pick<PaymentMethodType, 'id' | 'isDefault' | 'stripePaymentMethodId' | 'cardDetails'>[] = paymentMethodsData?.myPaymentMethods || [];
    const stripePublicKey: string = stripeKeyData?.getTenantStripePublicKey || "";
    const clientSecret: string = setupIntentData?.createSetupIntent || "";
    const hasCard = paymentMethods.length > 0;
    const defaultCard = paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];

    usePageTitle(product ? `${product.name} Plan` : "Subscribe");

    // When transitioning to payment step, create setup intent if no card
    useEffect(() => {
        if (step === 'payment' && isAuthenticated && user?.id && !hasCard && !clientSecret && !creatingSetup) {
            createSetupIntent({ variables: { clientId: user.id } });
        }
    }, [step, isAuthenticated, user?.id, hasCard, clientSecret, creatingSetup, createSetupIntent]);

    // Reload-resilience #1 — auto-apply a coupon passed in the URL (?coupon=CODE) once the
    // product has loaded, so a reload (or a shared promo link) re-applies it without retyping.
    useEffect(() => {
        const urlCoupon = searchParams.get('coupon');
        if (urlCoupon && product?.id && !appliedCoupon) {
            setCouponInput(urlCoupon);
            applyCode(urlCoupon);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id]);

    // Reload-resilience #2 — if the user is already enrolled in this product's course (checked
    // against the server, not browser storage), land them on the "you're in" success state
    // instead of the price page. Covers reload-after-activation and revisits.
    useEffect(() => {
        if (step !== 'info' || !isAuthenticated || !product?.linkedCourseId) return;
        const enrollments = myEnrollmentsData?.lmGetMyEnrollments || [];
        if (enrollments.some((e: { courseId?: string }) => e.courseId === product.linkedCourseId)) {
            setActivationResult({ courseStartUrl: product.courseStartUrl || undefined });
            setStep('success');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myEnrollmentsData, product?.linkedCourseId, isAuthenticated, step]);

    // Effective price after any applied coupon THEN any reward tokens (drives the
    // price display + free path). Tokens only make sense against an actual charge —
    // the backend guards the same way (product.chargeOnActivation).
    const tokenValueAud = tokenConfigData?.publicRewardTokenConfig?.tokenValue || 50;
    const activeTokenBalance = rewardBalanceData?.myRewardBalance?.activeTokens || 0;
    const priceAfterCoupon = appliedCoupon ? appliedCoupon.finalAmount : (product?.amount ?? 0);
    const canUseTokens = !!product?.chargeOnActivation && activeTokenBalance > 0;
    const maxUsefulTokens = canUseTokens ? Math.min(activeTokenBalance, Math.max(1, Math.ceil(priceAfterCoupon / tokenValueAud))) : 0;
    const tokenDiscount = canUseTokens ? tokenCountToApply * tokenValueAud : 0;
    const effectivePrice = Math.max(0, priceAfterCoupon - tokenDiscount);
    const isFreeAfterCoupon = effectivePrice <= 0 && (!!appliedCoupon || tokenCountToApply > 0);

    // ─── Handlers ───────────────────────────────────────────────────────────

    const handleGetStarted = () => {
        if (!isAuthenticated) {
            setShowLoginModal(true);
        } else if (isFreeAfterCoupon) {
            // 100%-off coupon → no card needed, activate straight away.
            handleActivate();
        } else {
            setStep('payment');
        }
    };

    const handleLoginSuccess = (token: string) => {
        localStorage.setItem('auth_token', token);
        refreshAuth();
        setShowLoginModal(false);
        // Free (fully-coupon-covered) → activate directly; otherwise collect a card.
        if (isFreeAfterCoupon) {
            handleActivate();
        } else {
            setStep('payment');
        }
    };

    // Unknown email at checkout → the login modal hands us the verified email + code and
    // closes itself; we open a small name-capture step instead of the dead-end card.
    const handleAccountNotFound = async ({ identifier, code }: { method: 'phone' | 'email' | 'username'; identifier: string; code?: string }) => {
        setShowLoginModal(false);
        setSignupFName("");
        setSignupLName("");
        setSignupPhone("");
        setPendingSignup({ email: identifier, code: code || "" });
    };

    // Create the account from the verified email + captured name, log in, then resume the
    // original action (free → activate; paid → collect a card).
    const handleCompleteSignup = async () => {
        if (!pendingSignup || !signupFName.trim() || !signupLName.trim()) return;
        try {
            const { data: res } = await registerWithVerifiedEmail({
                variables: {
                    input: {
                        email: pendingSignup.email,
                        code: pendingSignup.code,
                        fName: signupFName.trim(),
                        lName: signupLName.trim(),
                        phoneNumber: signupPhone.trim() || undefined,
                    },
                },
            });
            const result = res?.registerWithVerifiedEmail;
            if (result?.token) {
                localStorage.setItem('auth_token', result.token);
                refreshAuth();
                setPendingSignup(null);
                // Passwordless new account → nudge a passcode on the success screen.
                setShowPasscodeNudge(result.accountStatus === 'CREATED_NEW');
                if (isFreeAfterCoupon) {
                    handleActivate();
                } else {
                    setStep('payment');
                }
            } else {
                toast({ title: "Couldn't create your account", description: "Please try again or contact support.", status: "error", duration: 5000 });
            }
        } catch (e) {
            toast({ title: "Signup failed", description: e instanceof Error ? e.message : "Please try again.", status: "error", duration: 5000 });
        }
    };

    // Inline passkey enrolment — runs the WebAuthn ceremony in place (the OS Face ID / Touch ID
    // prompt appears over this page), so the user never leaves the success screen or loses the
    // course hand-off. "Start Your Course" stays available whether or not they add a passkey.
    const handleSetupPasskey = async () => {
        setPasskeyBusy(true);
        try {
            const label = `${user?.fName || ''} ${user?.lName || ''}`.trim();
            await enrollPasskey(generatePasskeyRegOptions, verifyPasskeyRegistration, label ? `${label}'s device` : 'My device');
            setPasskeyAdded(true);
            toast({ title: "Passkey added", description: "You can sign in instantly next time.", status: "success", duration: 3500 });
        } catch (err) {
            toast({ title: "Passkey setup", description: friendlyPasskeyError(err), status: "error", duration: 5000 });
        } finally {
            setPasskeyBusy(false);
        }
    };

    const handlePaymentMethodSaved = async (stripePaymentMethodId: string, stripeCustomerId?: string) => {

        // Save PaymentMethod record to MongoDB so it shows on Payment Methods & Manual Charging pages
        try {
            await createPaymentMethod({
                variables: {
                    input: {
                        clientId: user?.id,
                        stripePaymentMethodId,
                        stripeCustomerId: stripeCustomerId || undefined,
                        type: 'CARD',
                        isDefault: true,
                    }
                }
            });
        } catch (pmErr) {
            console.error('[SubscribePage] Failed to save PaymentMethod to MongoDB:', pmErr);
            // Non-fatal — continue with subscription activation
        }

        setSavedPaymentMethodId(stripePaymentMethodId);
        refetchPaymentMethods();
        handleActivate();
    };

    const applyCode = async (code: string) => {
        if (!code || !product?.id) return;
        setCouponError(null);
        try {
            const { data: res } = await validateCoupon({ variables: { code, productId: product.id } });
            const v = res?.validateCoupon;
            if (v?.valid) {
                setAppliedCoupon({ code: v.code, discountApplied: v.discountApplied ?? 0, finalAmount: v.finalAmount ?? product.amount });
                setCouponError(null);
                // Reflect the applied coupon into the URL — survives reload + makes promo links shareable.
                const next = new URLSearchParams(searchParams);
                next.set('coupon', v.code);
                setSearchParams(next, { replace: true });
            } else {
                setAppliedCoupon(null);
                setCouponError(v?.reason || "That code isn't valid");
            }
        } catch (e) {
            setAppliedCoupon(null);
            setCouponError(e instanceof Error ? e.message : "Couldn't check that code");
        }
    };

    const handleApplyCoupon = () => applyCode(couponInput.trim());

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput("");
        setCouponError(null);
        const next = new URLSearchParams(searchParams);
        next.delete('coupon');
        setSearchParams(next, { replace: true });
    };

    const handleActivate = async () => {
        if (!product?.id) return;
        setStep('activating');

        try {
            const { data: result } = await createLocalSubscription({
                variables: {
                    subscriptionProductId: product.id,
                    couponCode: appliedCoupon?.code || undefined,
                    tokenCount: tokenCountToApply > 0 ? tokenCountToApply : undefined,
                },
            });

            const activation = result?.createLocalSubscription;
            if (activation?.success) {
                // Activation just granted permissions server-side (e.g. LM_STUDENT). Re-fetch the
                // client so AuthContext.user.permissions is fresh before the course route checks it
                // — otherwise "Start Your Course" hits Access Denied on a stale permission set.
                await refreshAuth();
                setActivationResult({
                    subscriptionId: activation.subscriptionId,
                    enrollmentId: activation.enrollmentId,
                    courseStartUrl: activation.courseStartUrl,
                });
                setStep('success');
                toast({ title: "Subscription activated!", status: "success", duration: 5000 });
            } else {
                console.error('[SubscribePage] Activation failed:', activation?.error);
                toast({
                    title: "Activation failed",
                    description: activation?.error || "Something went wrong",
                    status: "error",
                    duration: 5000,
                });
                setStep('payment');
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Failed to activate subscription";
            console.error('[SubscribePage] Activation error:', message);
            toast({
                title: "Error",
                description: message,
                status: "error",
                duration: 5000,
            });
            setStep('payment');
        }
    };

    // ─── Colors (module pattern — see vault/how-to/color-mode-pattern.md) ───
    // Use Chakra's useColorModeValue + brandConfig's lightTheme/darkTheme objects
    // directly. This is the most reliable pattern: useColorModeValue is Chakra's
    // official hook (re-renders on toggle, no localStorage middleman), and reading
    // from lightTheme.X / darkTheme.X straight bypasses getColor's fallback chain.
    // Light mode → light bg + dark text. Dark mode → dark bg + light text.

    const { colorMode } = useColorMode();
    const textPrimary = useColorModeValue(lightTheme.text.primary, darkTheme.text.primary);
    const textMuted = useColorModeValue(lightTheme.text.muted, darkTheme.text.muted);
    const cardBg = useColorModeValue(lightTheme.background.cardGradient, darkTheme.background.cardGradient);
    const cardBorder = useColorModeValue(lightTheme.border.darkCard, darkTheme.border.darkCard);
    const errorTextColor = useColorModeValue("red.500", "red.300");
    const accentColor = product?.color || brandConfig.colors.primary;

    // ─── Loading / Error States ─────────────────────────────────────────────

    if (loading) {
        return (
            <VStack textAlign="center">
                <Spinner size="xl" color={brandConfig.colors.primary} />
                <Text mt={4} color={textMuted}>Loading plan details...</Text>
            </VStack>
        );
    }

    if (error || !product) {
        return (
            <>
                <Alert status="warning" borderRadius="lg">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold">Plan not found</Text>
                        <Text fontSize="sm">
                            The subscription plan "{slug}" doesn't exist or is no longer available.
                        </Text>
                    </Box>
                </Alert>
                <Button mt={6} onClick={() => navigate("/profile/subscription-offers")} variant="outline">
                    View All Plans
                </Button>
            </>
        );
    }

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <>
                <VStack spacing={{ base: 6, md: 10 }} align="stretch">
                    {/* ── Hero banner ── solid brand-accent bg so white text is readable in both modes.
                        Used to use Chakra `bgGradient` shorthand with hex+alpha (#007AFFCC) but Chakra's
                        gradient parser doesn't accept 8-digit hex; bg fell back to transparent → white-on-white
                        invisible in light mode. Plain `bg` with the solid accent always renders. */}
                    <Box
                        bg={accentColor}
                        backgroundImage={`linear-gradient(135deg, ${accentColor} 0%, ${accentColor} 60%, rgba(0,0,0,0.15) 100%)`}
                        borderRadius="3xl"
                        px={{ base: 6, md: 12 }}
                        py={{ base: 10, md: 14 }}
                        textAlign="center"
                        boxShadow={colorMode === 'light'
                            ? "0 10px 40px rgba(0,0,0,0.08)"
                            : "0 10px 40px rgba(0,0,0,0.5)"}
                        position="relative"
                        overflow="hidden"
                    >
                        <Box
                            position="absolute"
                            top="-30%"
                            right="-10%"
                            w="400px"
                            h="400px"
                            bg="whiteAlpha.200"
                            borderRadius="full"
                            filter="blur(80px)"
                            pointerEvents="none"
                        />
                        <VStack spacing={4} position="relative" zIndex={1}>
                            {product.badge && (
                                <Badge
                                    bg="whiteAlpha.300"
                                    color="white"
                                    fontSize="sm"
                                    px={4}
                                    py={1}
                                    borderRadius="full"
                                    backdropFilter="blur(8px)"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                >
                                    {product.badge}
                                </Badge>
                            )}
                            <Heading
                                size={{ base: "xl", md: "2xl" }}
                                color="white"
                                lineHeight="1.1"
                                letterSpacing="-0.5px"
                            >
                                {product.name}
                            </Heading>
                            {product.description && (
                                <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" maxW="600px">
                                    {product.description}
                                </Text>
                            )}
                            <Badge
                                bg="whiteAlpha.400"
                                color="white"
                                fontSize="sm"
                                px={3}
                                py={1}
                                borderRadius="full"
                                mt={2}
                            >
                                ⚡ Instant access
                            </Badge>
                        </VStack>
                    </Box>

                    {/* ── Trust strip ── adaptive surface (cardGradient adapts; .surface does not) */}
                    <Box
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor={cardBorder}
                        px={{ base: 4, md: 6 }}
                        py={4}
                    >
                        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={{ base: 3, md: 6 }}>
                            {[
                                { icon: LockIcon, label: "Secure Stripe Checkout" },
                                { icon: RepeatIcon, label: product.chargeOnActivation ? "One-off payment" : "Cancel anytime" },
                                { icon: ChatIcon, label: "Real human support" },
                            ].map((item, i) => (
                                <HStack key={i} justify="center" spacing={2}>
                                    <Icon as={item.icon} color={accentColor} boxSize={4} />
                                    <Text fontSize="sm" color={textPrimary} fontWeight="medium">
                                        {item.label}
                                    </Text>
                                </HStack>
                            ))}
                        </SimpleGrid>
                    </Box>

                    {/* ── Plan Selector (pill group ≤4, dropdown otherwise) ── */}
                    {allProducts.length > 1 && step === 'info' && (
                        allProducts.length <= 4 ? (
                            <Wrap justify="center" spacing={2}>
                                {allProducts.map((p) => {
                                    const isActive = p.slug === slug;
                                    return (
                                        <WrapItem key={p.slug}>
                                            <Button
                                                onClick={() => navigate(`/subscribe/${p.slug}`)}
                                                bg={isActive ? accentColor : "transparent"}
                                                color={isActive ? "white" : textPrimary}
                                                border="2px solid"
                                                borderColor={accentColor}
                                                _hover={{
                                                    bg: isActive ? accentColor : `${accentColor}22`,
                                                    transform: "translateY(-1px)",
                                                }}
                                                borderRadius="full"
                                                size="md"
                                                fontWeight="semibold"
                                                transition="all 0.15s"
                                            >
                                                {p.name} · ${p.amount.toLocaleString()}{p.chargeOnActivation ? "" : `/${p.interval}`}
                                            </Button>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                        ) : (
                            <Select
                                value={slug}
                                onChange={(e) => navigate(`/subscribe/${e.target.value}`)}
                                maxW="400px"
                                mx="auto"
                                color={textPrimary}
                                borderColor={accentColor}
                            >
                                {allProducts.map((p) => (
                                    <option key={p.slug} value={p.slug}>
                                        {p.name} — ${p.amount} {p.chargeOnActivation ? "one-off (card on file)" : `/${p.interval}`} {p.currency}
                                    </option>
                                ))}
                            </Select>
                        )
                    )}

                    {/* Pricing Card — explicit bg so CardBody text stays readable in both modes */}
                    <Card
                        maxW="520px"
                        mx="auto"
                        w="100%"
                        bg={cardBg}
                        border="2px solid"
                        borderColor={accentColor}
                        borderRadius="2xl"
                        overflow="hidden"
                        boxShadow={colorMode === 'light'
                            ? "0 12px 50px rgba(0,0,0,0.1)"
                            : "0 12px 50px rgba(0,0,0,0.5)"}
                        transition="all 0.3s ease"
                        _hover={{
                            transform: "translateY(-4px)",
                            boxShadow: colorMode === 'light'
                                ? "0 20px 60px rgba(0,0,0,0.15)"
                                : "0 20px 60px rgba(0,0,0,0.6)",
                        }}
                    >
                        {/* Price header with radial highlight — solid brand bg so white text always reads */}
                        <Box
                            bg={accentColor}
                            backgroundImage={`radial-gradient(circle at top center, rgba(255,255,255,0.18), transparent 70%)`}
                            py={7}
                            textAlign="center"
                            position="relative"
                            overflow="hidden"
                        >
                            <Box
                                position="absolute"
                                top="-40%"
                                left="50%"
                                transform="translateX(-50%)"
                                w="300px"
                                h="300px"
                                bg="whiteAlpha.200"
                                borderRadius="full"
                                filter="blur(60px)"
                                pointerEvents="none"
                            />
                            <HStack justify="center" align="baseline" spacing={1} position="relative" zIndex={1}>
                                <Text fontSize="lg" color="whiteAlpha.900" fontWeight="semibold">$</Text>
                                <Text
                                    fontSize={{ base: "5xl", md: "6xl" }}
                                    fontWeight="extrabold"
                                    color="white"
                                    lineHeight="1"
                                    letterSpacing="-2px"
                                >
                                    {effectivePrice.toLocaleString()}
                                </Text>
                                <Text fontSize="md" color="whiteAlpha.900" fontWeight="medium">
                                    {product.chargeOnActivation ? "one-off" : `/${product.interval}`}
                                </Text>
                            </HStack>
                            {appliedCoupon && (
                                <HStack justify="center" spacing={2} mt={1} position="relative" zIndex={1}>
                                    <Text fontSize="sm" color="whiteAlpha.700" as="s">${product.amount.toLocaleString()}</Text>
                                    <Badge colorScheme="green" variant="solid">
                                        {isFreeAfterCoupon ? "FREE" : `−$${appliedCoupon.discountApplied.toLocaleString()}`} · {appliedCoupon.code}
                                    </Badge>
                                </HStack>
                            )}
                            <Text fontSize="sm" color="whiteAlpha.800" mt={1} position="relative" zIndex={1}>
                                {product.currency.toUpperCase()}
                                {product.trialDays ? ` \u00b7 ${product.trialDays}-day free trial` : ""}
                                {product.chargeOnActivation ? " \u00b7 card on file for future billing" : ""}
                            </Text>
                        </Box>

                        <CardBody py={8} px={{ base: 5, md: 8 }}>
                            <VStack spacing={6} align="stretch">
                                {/* Detailed description */}
                                {product.detailedDescription && (
                                    <>
                                        <Text color={textPrimary} fontSize="sm" lineHeight="tall">
                                            {product.detailedDescription}
                                        </Text>
                                        <Divider />
                                    </>
                                )}

                                {/* Features */}
                                {product.features?.length > 0 && (
                                    <Box>
                                        <Text fontWeight="bold" color={textPrimary} mb={3} fontSize="sm">
                                            What's included:
                                        </Text>
                                        <List spacing={3}>
                                            {product.features.map((feature: string, i: number) => (
                                                <ListItem key={i} display="flex" alignItems="flex-start" fontSize="sm">
                                                    <ListIcon as={CheckCircleIcon} color={accentColor} mt="2px" boxSize={4} />
                                                    <Text color={textPrimary}>{feature}</Text>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {/* Tier badge */}
                                <HStack justify="center">
                                    <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="full">
                                        {tierWords(product.tier)} tier
                                    </Badge>
                                </HStack>

                                {/* ── Promo / coupon code ── */}
                                {step === 'info' && (
                                    <Box>
                                        <Text fontSize="sm" color={textMuted} mb={2}>Have a promo code?</Text>
                                        {appliedCoupon ? (
                                            <HStack justify="space-between" p={2} borderWidth="1px" borderRadius="md" borderColor={accentColor}>
                                                <HStack spacing={2}>
                                                    <Icon as={CheckCircleIcon} color="green.400" />
                                                    <Text fontSize="sm" color={textPrimary} fontWeight="medium">
                                                        {appliedCoupon.code} applied{isFreeAfterCoupon ? " — free access" : ` — −$${appliedCoupon.discountApplied.toLocaleString()}`}
                                                    </Text>
                                                </HStack>
                                                <Button size="xs" variant="ghost" onClick={removeCoupon}>Remove</Button>
                                            </HStack>
                                        ) : (
                                            <HStack>
                                                <Input
                                                    size="sm"
                                                    placeholder="Enter code"
                                                    value={couponInput}
                                                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                                    onKeyDown={(e) => { if (e.key === "Enter") handleApplyCoupon(); }}
                                                    textTransform="uppercase"
                                                />
                                                <Button size="sm" onClick={handleApplyCoupon} isLoading={couponChecking} isDisabled={!couponInput.trim()}>
                                                    Apply
                                                </Button>
                                            </HStack>
                                        )}
                                        {couponError && <Text fontSize="sm" color={errorTextColor} mt={1}>{couponError}</Text>}
                                    </Box>
                                )}

                                {/* ── Pay with reward tokens (optional, stacks with a coupon) ── */}
                                {step === 'info' && canUseTokens && (
                                    <Box>
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="sm" color={textMuted}>
                                                Have reward tokens? ({activeTokenBalance} available · AU${tokenValueAud} each)
                                            </Text>
                                            {tokenCountToApply > 0 && (
                                                <Button size="xs" variant="ghost" onClick={() => setTokenCountToApply(0)}>Remove</Button>
                                            )}
                                        </HStack>
                                        <HStack justify="center" spacing={3} p={2} borderWidth="1px" borderRadius="md" borderColor={tokenCountToApply > 0 ? accentColor : cardBorder}>
                                            <IconButton
                                                aria-label="Use fewer tokens"
                                                icon={<MinusIcon boxSize={2.5} />}
                                                size="xs"
                                                variant="outline"
                                                isDisabled={tokenCountToApply <= 0}
                                                onClick={() => setTokenCountToApply((n) => Math.max(0, n - 1))}
                                            />
                                            <Text fontSize="sm" fontWeight="bold" color={textPrimary} minW="90px" textAlign="center">
                                                {tokenCountToApply} token{tokenCountToApply !== 1 ? "s" : ""}
                                                {tokenCountToApply > 0 && ` (−$${Math.min(tokenDiscount, priceAfterCoupon).toLocaleString()})`}
                                            </Text>
                                            <IconButton
                                                aria-label="Use more tokens"
                                                icon={<AddIcon boxSize={2.5} />}
                                                size="xs"
                                                variant="outline"
                                                isDisabled={tokenCountToApply >= maxUsefulTokens}
                                                onClick={() => setTokenCountToApply((n) => Math.min(maxUsefulTokens, n + 1))}
                                            />
                                        </HStack>
                                    </Box>
                                )}

                                {/* ── Step: Info (default) ── */}
                                {step === 'info' && (
                                    <>
                                        <Button
                                            size="lg"
                                            bg={accentColor}
                                            color="white"
                                            _hover={{ opacity: 0.9, transform: "translateY(-1px)" }}
                                            borderRadius="xl"
                                            h="56px"
                                            fontSize="lg"
                                            onClick={handleGetStarted}
                                            transition="all 0.2s"
                                        >
                                            {isFreeAfterCoupon ? "Get free access" : (product.ctaText || "Get Started")}
                                        </Button>
                                        <Text fontSize="sm" color={textMuted} textAlign="center">
                                            {product.chargeOnActivation
                                                ? "One-off payment. Card saved on file for future billing."
                                                : "Cancel anytime. No lock-in contracts."}
                                        </Text>
                                    </>
                                )}

                                {/* ── Step: Payment ── */}
                                {step === 'payment' && isAuthenticated && (
                                    <VStack spacing={4} align="stretch">
                                        <Divider />
                                        <Text fontWeight="bold" color={textPrimary} fontSize="sm">
                                            {hasCard ? "Your Payment Method" : "Add Payment Method"}
                                        </Text>

                                        {hasCard && defaultCard ? (
                                            <VStack spacing={4}>
                                                <HStack
                                                    p={4}
                                                    borderRadius="lg"
                                                    border="1px solid"
                                                    borderColor="green.300"
                                                    bg={colorMode === 'dark' ? 'rgba(48, 209, 88, 0.1)' : 'green.50'}
                                                    w="100%"
                                                    justify="space-between"
                                                >
                                                    <HStack>
                                                        <Text fontWeight="medium" color={textPrimary}>
                                                            {defaultCard.cardDetails?.brand?.toUpperCase() || "CARD"}
                                                        </Text>
                                                        <Text color={textMuted}>
                                                            ending in {defaultCard.cardDetails?.last4 || "****"}
                                                        </Text>
                                                    </HStack>
                                                    <CheckCircleIcon color="green.500" />
                                                </HStack>
                                                <Button
                                                    size="lg"
                                                    bg={accentColor}
                                                    color="white"
                                                    _hover={{ opacity: 0.9, transform: "translateY(-1px)" }}
                                                    borderRadius="xl"
                                                    h="56px"
                                                    fontSize="lg"
                                                    w="100%"
                                                    onClick={() => handleActivate()}
                                                    isLoading={activating}
                                                    loadingText="Activating..."
                                                    transition="all 0.2s"
                                                >
                                                    Activate Subscription
                                                </Button>
                                                <HStack justify="center" spacing={1.5}>
                                                    <Icon as={LockIcon} color={textMuted} boxSize={3} />
                                                    <Text fontSize="sm" color={textMuted}>
                                                        Your card is encrypted and stored by Stripe
                                                    </Text>
                                                </HStack>
                                            </VStack>
                                        ) : (
                                            <>
                                                {creatingSetup && (
                                                    <HStack justify="center" py={4}>
                                                        <Spinner size="sm" />
                                                        <Text fontSize="sm" color={textMuted}>Setting up payment...</Text>
                                                    </HStack>
                                                )}
                                                {clientSecret && stripePublicKey && (
                                                    <StripePaymentMethodSetup
                                                        clientSecret={clientSecret}
                                                        stripePublicKey={stripePublicKey}
                                                        clientName={`${user?.fName || ''} ${user?.lName || ''}`.trim() || 'Customer'}
                                                        onSuccess={(paymentMethodId, customerId) => handlePaymentMethodSaved(paymentMethodId, customerId)}
                                                        onError={(errorMsg) => {
                                                            toast({
                                                                title: "Payment method error",
                                                                description: errorMsg,
                                                                status: "error",
                                                                duration: 5000,
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            color={textMuted}
                                            onClick={() => setStep('info')}
                                        >
                                            Back
                                        </Button>
                                    </VStack>
                                )}

                                {/* ── Step: Activating ── */}
                                {step === 'activating' && (
                                    <VStack spacing={4} py={6}>
                                        <Spinner size="lg" color={accentColor} />
                                        <Text color={textPrimary} fontWeight="bold">Setting up your account...</Text>
                                        <Text color={textMuted} fontSize="sm">Activating your subscription...</Text>
                                    </VStack>
                                )}

                                {/* ── Step: Success ── */}
                                {step === 'success' && (
                                    <VStack spacing={4} align="stretch">
                                        <Divider />
                                        <Alert status="success" borderRadius="lg">
                                            <AlertIcon />
                                            <Box>
                                                <Text fontWeight="bold">You're subscribed!</Text>
                                                <Text fontSize="sm">
                                                    Your {product.name} subscription is now active.
                                                </Text>
                                            </Box>
                                        </Alert>

                                        {showPasscodeNudge && (
                                            <Alert status={passkeyAdded ? "success" : "info"} borderRadius="lg" alignItems="flex-start">
                                                <AlertIcon />
                                                <Box flex="1">
                                                    {passkeyAdded ? (
                                                        <>
                                                            <Text fontWeight="bold" fontSize="sm">Passkey added ✓</Text>
                                                            <Text fontSize="sm">You can now sign in instantly with Face ID / Touch ID next time.</Text>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Text fontWeight="bold" fontSize="sm">Set up a passkey for instant sign-in</Text>
                                                            <Text fontSize="sm" mb={2}>
                                                                Your account uses email codes. Add a passkey (Face ID / Touch ID / Windows Hello) so you can get back in with one tap — you'll stay right here.
                                                            </Text>
                                                            {browserSupportsWebAuthn() ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={handleSetupPasskey}
                                                                    isLoading={passkeyBusy}
                                                                    loadingText="Waiting for your device…"
                                                                >
                                                                    Set up a passkey
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => window.open("/security", "_blank", "noopener")}
                                                                >
                                                                    Manage sign-in (opens in a new tab)
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Alert>
                                        )}

                                        {activationResult?.courseStartUrl && (
                                            <Button
                                                size="lg"
                                                bg={accentColor}
                                                color="white"
                                                _hover={{ opacity: 0.9 }}
                                                borderRadius="xl"
                                                h="56px"
                                                fontSize="lg"
                                                onClick={() => navigate(activationResult.courseStartUrl!)}
                                            >
                                                Start Your Course
                                            </Button>
                                        )}

                                        <Button
                                            variant="outline"
                                            borderRadius="xl"
                                            onClick={() => navigate("/")}
                                        >
                                            Go to Dashboard
                                        </Button>
                                    </VStack>
                                )}
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* ── Social proof row (info step only) ── */}
                    {step === 'info' && (
                        <VStack spacing={2} pt={2}>
                            <HStack spacing={1}>
                                {[...Array(5)].map((_, i) => (
                                    <Text key={i} fontSize="lg" color={getColor("status.warning", colorMode)}>★</Text>
                                ))}
                            </HStack>
                            <Text fontSize="sm" color={textMuted} textAlign="center" maxW="480px">
                                Trusted by growing businesses across Australia · Powered by Stripe
                            </Text>
                        </VStack>
                    )}

                </VStack>

            {/* Login Modal */}
            <UnifiedLoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onSuccess={handleLoginSuccess}
                onAccountNotFound={handleAccountNotFound}
            />

            {/* Seamless signup — name capture for a freshly verified, not-yet-registered email */}
            <Modal isOpen={!!pendingSignup} onClose={() => setPendingSignup(null)} isCentered size={{ base: "full", md: "md" }}>
                <ModalOverlay />
                <ModalContent maxH={{ base: "100dvh", md: "85vh" }} overflowY="auto">
                    <ModalHeader>Create your account</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" color={textMuted}>
                                We've verified <b>{pendingSignup?.email}</b>. Just your name to finish — no password needed.
                            </Text>
                            <FormControl isRequired>
                                <FormLabel>First name</FormLabel>
                                <Input
                                    value={signupFName}
                                    onChange={(e) => setSignupFName(e.target.value)}
                                    placeholder="First name"
                                    autoFocus
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Last name</FormLabel>
                                <Input
                                    value={signupLName}
                                    onChange={(e) => setSignupLName(e.target.value)}
                                    placeholder="Last name"
                                    onKeyDown={(e) => { if (e.key === 'Enter' && signupFName.trim() && signupLName.trim()) handleCompleteSignup(); }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>
                                    Phone <Text as="span" color={textMuted} fontSize="sm">(optional)</Text>
                                </FormLabel>
                                <Input
                                    value={signupPhone}
                                    onChange={(e) => setSignupPhone(e.target.value)}
                                    placeholder="+61…"
                                    type="tel"
                                />
                            </FormControl>
                            <Button
                                bg={accentColor}
                                color="white"
                                _hover={{ opacity: 0.9 }}
                                borderRadius="xl"
                                h="48px"
                                onClick={handleCompleteSignup}
                                isLoading={registering}
                                loadingText="Creating account…"
                                isDisabled={!signupFName.trim() || !signupLName.trim()}
                            >
                                {isFreeAfterCoupon ? "Create account & get free access" : "Create account & continue"}
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default SubscribePage;
